/**
 * Sentry init for the frontend.
 *
 * Gated on VITE_SENTRY_DSN so the app works fine in local dev (or before the
 * Vercel→Sentry Marketplace integration is set up) — when the DSN is empty
 * this function no-ops and we save the bundle cost of the SDK runtime.
 *
 * Env vars (set by the Vercel→Sentry integration on production / preview):
 *   VITE_SENTRY_DSN          — required to enable
 *   VITE_SENTRY_ENVIRONMENT  — e.g. 'production' | 'preview' | 'development'
 *   VITE_SENTRY_RELEASE      — commit SHA; auto-set by Vercel integration
 *   VITE_SENTRY_TRACES_SAMPLE_RATE — 0..1, default 0.1 (10 % of requests)
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  // Dynamic import keeps the SDK out of the critical first-paint path.
  void import('@sentry/react').then((Sentry) => {
    try {
      Sentry.init({
        dsn,
        environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
        release: import.meta.env.VITE_SENTRY_RELEASE,
        // Capture 10 % of transactions for performance traces by default; the
        // value is overridable per env so prod can be tuned down if volume
        // becomes a cost concern.
        tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.1),
        // Send 0 % of session replays by default; flip to 1 in preview for
        // debugging. Replays are storage-heavy and we don't want PII at rest
        // without an explicit decision.
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,
        integrations: [
          Sentry.browserTracingIntegration(),
        ],
        // PII redaction — never let auth tokens or full email addresses
        // through unchecked. Defence-in-depth; Sentry also scrubs by default.
        beforeSend(event) {
          if (event.request?.headers) {
            for (const k of Object.keys(event.request.headers)) {
              if (/auth|cookie|token|apikey/i.test(k)) {
                event.request.headers[k] = '[redacted]';
              }
            }
          }
          return event;
        },
      });

      // Tag every event with the build commit so we can correlate to deploys.
      if (import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA) {
        Sentry.setTag('vercel_commit', String(import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA));
      }
    } catch {
      // Never let monitoring init break the app
    }
  }).catch(() => {});
}

/**
 * Identify the current user on the Sentry scope. Call this from the auth
 * provider once the user is loaded. Email is intentionally NOT included to
 * keep PII off the wire — id + org_id + role is enough to triage.
 */
export async function identifySentryUser(user: {
  id: string;
  org_id?: string | null;
  base_role?: string | null;
}) {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  try {
    const Sentry = await import('@sentry/react');
    Sentry.setUser({ id: user.id });
    if (user.org_id) Sentry.setTag('org_id', user.org_id);
    if (user.base_role) Sentry.setTag('role', user.base_role);
  } catch {
    // ignore
  }
}

export async function clearSentryUser() {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  try {
    const Sentry = await import('@sentry/react');
    Sentry.setUser(null);
  } catch {
    // ignore
  }
}
